/**
 * Module: screens/pantene/VideoUpload
 * Role: Pre-publication video upload screen for Pantene UGC flow.
 *       Shows a one-time checklist, handles file pick, upload progress,
 *       attempt counting, and error recovery.
 *       Pre-validates slot access via useFlow before entering any upload UI.
 *
 * Uses: api/pantene, api/flow-client, screens/pantene/_uploadXHR,
 *       react-router-dom, providers/PlatformProvider, components/atoms,
 *       screens/pantene/_pantene-atoms
 * Used by: App.tsx route /pantene/:slot_id/upload
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRequestUploadUrl, useTriggerAnalyze } from '../../api/pantene';
import { useFlow } from '../../api/flow-client';
import { uploadXHR } from './_uploadXHR';
import { usePlatform } from '../../providers/PlatformProvider';
import { IOSScreen, PageHeader, PressBtn, EmptyState, Ic, Card } from '../../components/atoms';
import { P, S, T } from '../../styles/tokens';
import { Spinner } from './_pantene-atoms';

// SYMMETRIC LIMIT — sync with src/shared/limits.py (MAX_VIDEO_SIZE_BYTES)
// and src/api/v2/buyer/_pantene_schemas.py (UploadUrlRequest.size_bytes le)
const MAX_VIDEO_SIZE_BYTES = 209_715_200; // 200 MB
const MAX_ATTEMPTS = 3;
const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
const CHECKLIST_KEY = 'pantene_checklist_seen_v1';

const CHECKLIST_ITEMS = [
  'Лицо в кадре',
  'Натуральные волосы',
  'Продукт виден',
  '30–60 сек, вертикальная съёмка',
];

interface ProgressState { loaded: number; total: number }

type Phase = 'checklist' | 'idle' | 'uploading' | 'triggering' | 'error' | 'max_attempts';

export default function VideoUpload() {
  const { slot_id: slotId } = useParams<{ slot_id: string }>();
  const navigate = useNavigate();

  // Pre-validate slot access before rendering upload UI (BUG-1038 S2).
  const { isLoading: slotLoading, error: slotError } = useFlow(slotId ?? '');

  const [phase, setPhase] = useState<Phase>(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem(CHECKLIST_KEY) ? 'idle' : 'checklist'
  );
  const [progress, setProgress] = useState<ProgressState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const pendingFileRef = useRef<File | null>(null);
  const pendingUploadUrlRef = useRef<string | null>(null);
  const pendingObjectPathRef = useRef<string | null>(null);
  const uploadUrlFetchedAtRef = useRef<number | null>(null);
  const abortCtrlRef = useRef<AbortController | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { capabilities, setMainButton, hideMainButton } = usePlatform();
  const { mutateAsync: requestUploadUrl } = useRequestUploadUrl();
  const { mutateAsync: triggerAnalyze } = useTriggerAnalyze(slotId ?? '');

  const attemptKey = `pantene_${slotId}_attempts`;
  const getAttempts = () => parseInt(localStorage.getItem(attemptKey) ?? '0', 10);
  const incAttempts = () => localStorage.setItem(attemptKey, String(getAttempts() + 1));

  const handleUploadTrigger = useCallback(() => fileInputRef.current?.click(), []);

  // Telegram MainButton wiring
  useEffect(() => {
    if (capabilities.fileUpload || phase !== 'idle') return;
    setMainButton('Загрузить', handleUploadTrigger);
    return () => { hideMainButton(); };
  }, [capabilities.fileUpload, phase, handleUploadTrigger, setMainButton, hideMainButton]);

  useEffect(() => () => abortCtrlRef.current?.abort(), []);

  const acknowledgeChecklist = () => { localStorage.setItem(CHECKLIST_KEY, '1'); setPhase('idle'); };

  const isUploadUrlStale = () => {
    const f = uploadUrlFetchedAtRef.current;
    return !f || Date.now() - f > 4.5 * 60 * 1000;
  };

  const runUpload = async (file: File) => {
    if (getAttempts() >= MAX_ATTEMPTS) { setPhase('max_attempts'); return; }
    setErrorMsg(null);
    try {
      let uploadUrl = pendingUploadUrlRef.current;
      let objectPath = pendingObjectPathRef.current;
      if (!uploadUrl || !objectPath || isUploadUrlStale() || pendingFileRef.current !== file) {
        setPhase('uploading'); setProgress({ loaded: 0, total: file.size });
        const resp = await requestUploadUrl({ filename: file.name, size_bytes: file.size, content_type: file.type });
        uploadUrl = resp.upload_url; objectPath = resp.object_path;
        pendingUploadUrlRef.current = uploadUrl; pendingObjectPathRef.current = objectPath;
        pendingFileRef.current = file; uploadUrlFetchedAtRef.current = Date.now();
      } else {
        setPhase('uploading'); setProgress({ loaded: 0, total: file.size });
      }
      abortCtrlRef.current = new AbortController();
      await uploadXHR({ url: uploadUrl, file, onProgress: (p) => setProgress(p), signal: abortCtrlRef.current.signal });
      setPhase('triggering');
      await triggerAnalyze({ object_path: objectPath });
      incAttempts();
      navigate(`/pantene/${slotId}/status`);
    } catch (err: unknown) {
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      if (!isAbort) { setErrorMsg('Не удалось загрузить видео.'); setPhase('error'); }
      else { setPhase('idle'); }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = '';
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setErrorMsg('Неподдерживаемый формат. Выберите MP4, MOV или WebM.'); setPhase('error'); return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      setErrorMsg('Файл слишком большой. Максимум 200 МБ.'); setPhase('error'); return;
    }
    void runUpload(file);
  };

  const retryUpload = () => {
    const file = pendingFileRef.current;
    if (file) { void runUpload(file); } else { setPhase('idle'); fileInputRef.current?.click(); }
  };

  // --- Render: slot loading/error gates (BUG-1038 S2) ---
  if (slotLoading) {
    return (
      <IOSScreen>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spinner size={40} /><span className="sr-only">Загрузка...</span>
        </div>
      </IOSScreen>
    );
  }

  if (slotError) {
    // BUG-1108: distinguish FLOW_NOT_FOUND from real errors.
    const status = (slotError as (Error & { status?: number }) | null)?.status;
    const msg = (slotError as Error | null)?.message ?? '';
    const text = status === 404 && msg.includes('FLOW_NOT_FOUND')
      ? 'Задание взято. Это задание выполняется в основном боте — вернись на главную.'
      : 'Задание недоступно. Возможно, ссылка устарела или открыта не тем аккаунтом.';
    return (
      <IOSScreen>
        <PageHeader title="Задание" onBack={() => navigate(-1)} />
        <div style={{ padding: '14px 16px 32px' }}>
          <Card p={16} r={16}>
            <p style={{ margin: 0, font: T.subhead, color: S.label }}>{text}</p>
            <div style={{ marginTop: 14 }}>
              <PressBtn onClick={() => navigate('/', { replace: true })}>На главную</PressBtn>
            </div>
          </Card>
        </div>
      </IOSScreen>
    );
  }

  if (phase === 'max_attempts') {
    return (
      <IOSScreen>
        <PageHeader title="Загрузка видео" onBack={() => navigate(-1)} />
        <EmptyState icon={<Ic name="warning" size={28} color={P.danger} />}
          title="Лимит попыток исчерпан" subtitle="Свяжитесь с оператором для получения помощи." />
      </IOSScreen>
    );
  }

  if (phase === 'checklist') {
    return (
      <IOSScreen>
        <PageHeader title="Перед съёмкой" onBack={() => navigate(-1)} />
        <div style={{ padding: '14px 16px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ margin: 0, font: T.subhead, color: S.secondaryLabel }}>
            Убедитесь, что ваше видео соответствует требованиям:
          </p>
          <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${P.line}`, overflow: 'hidden' }}>
            {CHECKLIST_ITEMS.map((item, i) => (
              <div key={item} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px',
                borderBottom: i < CHECKLIST_ITEMS.length - 1 ? `1px solid ${P.line}` : 'none',
              }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: 'rgba(47,143,111,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <Ic name="check" size={14} color={P.success} />
                </div>
                <span style={{ font: T.subhead, color: S.label }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: 'sticky', bottom: 0, padding: '12px 16px 32px',
          background: `linear-gradient(to top, ${P.cream} 70%, transparent)` }}>
          <PressBtn onClick={acknowledgeChecklist}>Понятно, начать</PressBtn>
        </div>
      </IOSScreen>
    );
  }

  if (phase === 'uploading' || phase === 'triggering') {
    const pct = progress && progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;
    return (
      <IOSScreen>
        <PageHeader title="Загрузка видео" />
        <div style={{ position: 'absolute', top: 96, bottom: 0, left: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '0 32px', gap: 24 }}>
          <div style={{ width: 80, height: 80, borderRadius: 24,
            backdropFilter: 'blur(16px) saturate(140%)', WebkitBackdropFilter: 'blur(16px) saturate(140%)',
            background: 'rgba(53,107,107,0.18)', border: '1px solid rgba(53,107,107,0.30)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.30) inset',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {phase === 'triggering'
              ? <Spinner color={P.terracotta} size={32} />
              : <Ic name="camera" size={32} color={P.terracotta} />}
          </div>
          <p style={{ margin: 0, font: T.headline, color: S.label, textAlign: 'center' }}>
            {phase === 'triggering' ? 'Отправляем на анализ...' : 'Загрузка видео...'}
          </p>
          {phase === 'uploading' && (
            <>
              <div style={{ width: '100%', height: 6, borderRadius: 3, background: P.line, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: P.terracotta,
                  borderRadius: 3, transition: 'width 0.2s ease' }} />
              </div>
              <p style={{ margin: 0, font: T.footnote, color: S.secondaryLabel }}>{pct}%</p>
            </>
          )}
        </div>
      </IOSScreen>
    );
  }

  if (phase === 'error') {
    return (
      <IOSScreen>
        <PageHeader title="Загрузка видео" onBack={() => setPhase('idle')} />
        <div style={{ position: 'absolute', top: 96, bottom: 0, left: 0, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '0 24px', gap: 20 }}>
          <EmptyState icon={<Ic name="warning" size={28} color={P.danger} />}
            title={errorMsg ?? 'Произошла ошибка'} />
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <PressBtn onClick={retryUpload}>Повторить</PressBtn>
            <PressBtn onClick={() => setPhase('idle')} variant="ghost" fg={S.secondaryLabel}>
              Выбрать другой файл
            </PressBtn>
          </div>
        </div>
      </IOSScreen>
    );
  }

  // phase === 'idle' — main upload zone
  return (
    <IOSScreen>
      <PageHeader title="Загрузить видео" onBack={() => navigate(-1)} />
      <div style={{ padding: '24px 16px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
        <button
          onClick={capabilities.fileUpload ? handleUploadTrigger : undefined}
          style={{
            width: '100%', borderRadius: 20, padding: '36px 24px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
            cursor: capabilities.fileUpload ? 'pointer' : 'default',
            border: '1.5px dashed rgba(53,107,107,0.45)',
            background: 'rgba(53,107,107,0.08)',
            backdropFilter: 'blur(16px) saturate(140%)',
            WebkitBackdropFilter: 'blur(16px) saturate(140%)',
            boxSizing: 'border-box',
          }}>
          <div style={{ width: 72, height: 72, borderRadius: '50%',
            background: 'rgba(192,81,58,0.12)', border: '1.5px solid rgba(192,81,58,0.28)',
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ic name="camera" size={32} color={P.terracotta} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, font: T.headline, color: S.label }}>Выберите видео</p>
            <p style={{ margin: '4px 0 0', font: T.footnote, color: S.secondaryLabel }}>MP4, MOV или WebM · до 200 МБ</p>
          </div>
          {capabilities.fileUpload && (
            <div style={{ height: 38, borderRadius: 19, background: P.terracotta, color: '#fff',
              padding: '0 20px', display: 'flex', alignItems: 'center', gap: 6,
              font: T.footnote, fontWeight: 600, boxShadow: '0 4px 14px rgba(192,81,58,0.30)' }}>
              <Ic name="camera" size={15} color="#ffffff" />
              Загрузить
            </div>
          )}
        </button>
        <p style={{ margin: 0, font: T.caption, color: S.tertiaryLabel, textAlign: 'center' }}>
          Вертикальная съёмка · лицо в кадре · 30–60 сек
        </p>
      </div>
      <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/webm"
        style={{ display: 'none' }} onChange={handleFileChange} />
    </IOSScreen>
  );
}
